import { useEffect, useState } from 'react';
import { Bell, CheckCheck, Info, AlertTriangle, CheckCircle2, AlertCircle, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { Notification, formatDate } from '@/lib/index';

const TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle2,
  alert: AlertCircle,
};

const TYPE_BORDER: Record<string, string> = {
  info: 'border-l-info',
  warning: 'border-l-warning',
  success: 'border-l-success',
  alert: 'border-l-danger',
};

const TYPE_ICON_COLOR: Record<string, string> = {
  info: 'text-info',
  warning: 'text-warning',
  success: 'text-success',
  alert: 'text-danger',
};

export default function DashboardNotifications() {
  const { profile } = useAuth();
  const { t } = useI18n();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    if (!profile) return;

    const fetchNotifications = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false });
      
      setNotifications((data ?? []) as Notification[]);
      setLoading(false);
    };

    fetchNotifications();

    const channel = supabase
      .channel('notifications_channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${profile.id}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
  };

  const markAllRead = async () => {
    if (!profile) return;
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', profile.id).eq('is_read', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const deleteNotification = async (id: string) => {
    await supabase.from('notifications').delete().eq('id', id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const formatTimeAgo = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Przed chwilą';
    if (diffMins < 60) return `${diffMins} min temu`;
    if (diffHours < 24) return `${diffHours} godz. temu`;
    if (diffDays < 7) return `${diffDays} dni temu`;
    
    return formatDate(dateStr);
  };

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter((n) => !n.is_read)
    : notifications;

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const allRead = unreadCount === 0;

  return (
    <DashboardLayout title={t('notif_title')}>
      <div className="max-w-4xl mx-auto space-y-6 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('notif_title')}</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  {unreadCount} nieprzeczytanych
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-muted/30 rounded-lg p-1">
              <Button
                variant={filter === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('all')}
                className="h-8 px-3"
              >
                Wszystkie
              </Button>
              <Button
                variant={filter === 'unread' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFilter('unread')}
                className="h-8 px-3"
              >
                Nieprzeczytane
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={markAllRead}
              disabled={allRead}
              className="gap-2 h-8"
            >
              <CheckCheck className="w-4 h-4" />
              Oznacz wszystkie
            </Button>
          </div>
        </div>

        <Card className="border-border/50">
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex gap-4 p-4 border-b border-border/30 last:border-0 animate-pulse">
                    <div className="w-10 h-10 bg-muted rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-full" />
                      <div className="h-2 bg-muted rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-muted/30 flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-10 h-10 text-muted-foreground/40" />
                </div>
                <p className="text-lg font-semibold text-foreground mb-1">{t('notif_empty')}</p>
                <p className="text-sm text-muted-foreground">Nie masz żadnych powiadomień</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                <AnimatePresence>
                  {filteredNotifications.map((n) => {
                    const Icon = TYPE_ICON[n.type] ?? Info;
                    const borderColor = TYPE_BORDER[n.type] ?? TYPE_BORDER.info;
                    const iconColor = TYPE_ICON_COLOR[n.type] ?? TYPE_ICON_COLOR.info;
                    
                    return (
                      <motion.div
                        key={n.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        onClick={() => !n.is_read && markRead(n.id)}
                        className={`flex gap-4 p-4 border-l-4 ${borderColor} transition-all cursor-pointer ${
                          n.is_read
                            ? 'bg-transparent opacity-60'
                            : 'bg-muted/20 hover:bg-muted/30'
                        }`}
                      >
                        <div className="shrink-0">
                          <div className={`w-10 h-10 rounded-lg bg-background border border-border/50 flex items-center justify-center ${iconColor}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <h3 className={`text-sm font-semibold ${
                              n.is_read ? 'text-muted-foreground' : 'text-foreground'
                            }`}>
                              {n.title}
                            </h3>
                            {!n.is_read && (
                              <Badge variant="secondary" className="bg-accent/20 text-accent text-xs border-accent/30 shrink-0">
                                Nowe
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                            {n.message}
                          </p>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs text-muted-foreground/70">
                              {formatTimeAgo(n.created_at)}
                            </p>
                            <div className="flex items-center gap-2">
                              {!n.is_read && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markRead(n.id);
                                  }}
                                  className="h-7 text-xs gap-1.5 text-accent hover:text-accent hover:bg-accent/10"
                                >
                                  <CheckCheck className="w-3.5 h-3.5" />
                                  Oznacz jako przeczytane
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(n.id);
                                }}
                                className="h-7 text-xs gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Usuń
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}